import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import supabase from '../supabase/supabase';
import { useStore } from '../context/StoreContext';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import { FiCheckCircle, FiPackage, FiArrowRight, FiClock, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LoadingSpinner from '../common/LoadingSpinner';

const MAX_RETRIES = 5;
const RETRY_DELAY = 3000;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useStore();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('verifying');
  const [marxMessage, setMarxMessage] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    const verifyPayment = async () => {
      if (processedRef.current) return;
      processedRef.current = true;

      const merchantRef = searchParams.get('merchantRID');

      if (!merchantRef) {
        setPaymentStatus('error');
        setLoading(false);
        return;
      }

      let attempts = 0;

      const attemptVerification = async () => {
        attempts++;

        try {
          const { data, error } = await supabase.functions.invoke('marx-verify', {
            body: { merchantReference: merchantRef }
          });

          if (error) {
            console.error('Verification error:', error);
            if (attempts < MAX_RETRIES) {
              setTimeout(attemptVerification, RETRY_DELAY);
              return;
            }
            setPaymentStatus('error');
            setLoading(false);
            return;
          }

          const result = data;
          setMarxMessage(result.marx_status || '');

          if (result.status === 'paid') {
            const { data: txData } = await supabase
              .from('payment_transactions')
              .select('order_id')
              .eq('merchant_reference', merchantRef)
              .maybeSingle();

            if (txData?.order_id) {
              const { data: orderData } = await supabase
                .from('orders')
                .select('*, order_items(*, products(name, image))')
                .eq('id', txData.order_id)
                .single();

              if (orderData) {
                setOrder(orderData);
                clearCart();
              }
            }

            setPaymentStatus('paid');
            setLoading(false);
          } else if (result.status === 'pending') {
            if (attempts < MAX_RETRIES) {
              setTimeout(attemptVerification, RETRY_DELAY);
            } else {
              const { data: txData } = await supabase
                .from('payment_transactions')
                .select('order_id')
                .eq('merchant_reference', merchantRef)
                .maybeSingle();

              if (txData?.order_id) {
                const { data: orderData } = await supabase
                  .from('orders')
                  .select('*, order_items(*, products(name, image))')
                  .eq('id', txData.order_id)
                  .single();

                if (orderData) {
                  setOrder(orderData);
                  clearCart();
                }
              }

              setPaymentStatus('processing');
              setLoading(false);
            }
          } else {
            setPaymentStatus('failed');
            setLoading(false);
          }
        } catch (err) {
          console.error('Verification exception:', err);
          if (attempts < MAX_RETRIES) {
            setTimeout(attemptVerification, RETRY_DELAY);
          } else {
            setPaymentStatus('error');
            setLoading(false);
          }
        }
      };

      await attemptVerification();
    };

    verifyPayment();
  }, [searchParams]);

  const handleManualRetry = async () => {
    setLoading(true);
    setPaymentStatus('verifying');
    processedRef.current = false;

    const merchantRef = searchParams.get('merchantRID');
    if (!merchantRef) {
      setPaymentStatus('error');
      setLoading(false);
      return;
    }

    try {
      const { data } = await supabase.functions.invoke('marx-verify', {
        body: { merchantReference: merchantRef }
      });

      if (data?.status === 'paid') {
        const { data: txData } = await supabase
          .from('payment_transactions')
          .select('order_id')
          .eq('merchant_reference', merchantRef)
          .maybeSingle();

        if (txData?.order_id) {
          const { data: orderData } = await supabase
            .from('orders')
            .select('*, order_items(*, products(name, image))')
            .eq('id', txData.order_id)
            .single();

          if (orderData) {
            setOrder(orderData);
            clearCart();
          }
        }
        setPaymentStatus('paid');
      } else {
        setPaymentStatus('processing');
      }
    } catch (err) {
      setPaymentStatus('processing');
    } finally {
      setLoading(false);
    }
  };

  if (loading || paymentStatus === 'verifying') {
    return (
      <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-24">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner size="xl" text="Verifying your payment..." />
        </div>
        <Footer />
      </div>
    );
  }

  if (paymentStatus === 'paid') {
    return (
      <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-24">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white/90 p-10 md:p-14 rounded-3xl border border-white shadow-xl text-center max-w-lg w-full">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-green-500" />
            </div>

            <h1 className="text-3xl font-serif text-brand-darkSage mb-3">Payment Successful!</h1>
            <p className="text-brand-lightText text-sm mb-8 leading-relaxed">
              Thank you for your purchase. Your payment has been confirmed and your order is being processed. We'll send you updates via email.
            </p>

            {order && (
              <div className="bg-brand-sand/50 p-5 rounded-2xl border border-brand-darkSage/10 mb-8 text-left">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-brand-gold">Order Number</span>
                  <span className="font-mono font-bold text-sm text-brand-darkSage">#{order.id.split('-')[0].toUpperCase()}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-brand-gold">Total Paid</span>
                  <span className="font-bold text-sm text-brand-darkSage">Rs. {Number(order.total_amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-brand-gold">Payment Status</span>
                  <span className="text-xs font-bold text-green-600 uppercase">Paid</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-brand-gold">Order Status</span>
                  <span className="text-xs font-bold text-blue-600 uppercase">Confirmed</span>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/')}
                className="flex-1 bg-brand-darkSage text-white py-3.5 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all flex items-center justify-center gap-2"
              >
                Continue Shopping
                <SafeIcon icon={FiArrowRight} size={14} />
              </button>
              {order && (
                <button
                  onClick={() => navigate(`/track/${order.id}`)}
                  className="flex-1 border-2 border-brand-darkSage text-brand-darkSage py-3.5 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-darkSage hover:text-white transition-all flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiPackage} size={14} />
                  Track Order
                </button>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (paymentStatus === 'processing') {
    return (
      <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-24">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white/90 p-10 md:p-14 rounded-3xl border border-white shadow-xl text-center max-w-lg w-full">
            <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiClock} className="w-10 h-10 text-amber-500" />
            </div>

            <h1 className="text-3xl font-serif text-brand-darkSage mb-3">Payment Processing</h1>
            <p className="text-brand-lightText text-sm mb-4 leading-relaxed">
              Your payment is being processed by the payment gateway. This can sometimes take a few minutes.
            </p>
            <p className="text-xs text-brand-lightText/70 mb-8">
              Please do not close this page. You will receive an email confirmation once your payment is confirmed.
            </p>

            {order && (
              <div className="bg-amber-50/50 p-5 rounded-2xl border border-amber-200/50 mb-8 text-left">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Order Number</span>
                  <span className="font-mono font-bold text-sm text-brand-darkSage">#{order.id.split('-')[0].toUpperCase()}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Amount</span>
                  <span className="font-bold text-sm text-brand-darkSage">Rs. {Number(order.total_amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Status</span>
                  <span className="text-xs font-bold text-amber-600 uppercase">Processing</span>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleManualRetry}
                className="flex-1 bg-brand-darkSage text-white py-3.5 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiRefreshCw} size={14} />
                Check Status Again
              </button>
              <button
                onClick={() => navigate('/')}
                className="flex-1 border-2 border-brand-darkSage text-brand-darkSage py-3.5 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-darkSage hover:text-white transition-all flex items-center justify-center gap-2"
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-24">
      <Navbar />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white/90 p-10 md:p-14 rounded-3xl border border-white shadow-xl text-center max-w-lg w-full">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiAlertCircle} className="w-10 h-10 text-red-400" />
          </div>

          <h1 className="text-3xl font-serif text-brand-darkSage mb-3">
            {paymentStatus === 'failed' ? 'Payment Unsuccessful' : 'Payment Could Not Be Verified'}
          </h1>
          <p className="text-brand-lightText text-sm mb-4 leading-relaxed">
            {paymentStatus === 'failed'
              ? 'Your payment could not be completed. No charges have been made to your account.'
              : 'We were unable to verify your payment status. This does not necessarily mean the payment failed.'
            }
          </p>

          {marxMessage && (
            <div className="bg-red-50/50 p-4 rounded-xl border border-red-100 mb-6">
              <p className="text-xs text-red-600 font-mono">Gateway Status: {marxMessage}</p>
            </div>
          )}

          <p className="text-xs text-brand-lightText/70 mb-8">
            If you see a charge on your account, it will be automatically reversed within 3-5 business days. 
            You can also try placing the order again.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleManualRetry}
              className="flex-1 bg-brand-darkSage text-white py-3.5 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiRefreshCw} size={14} />
              Retry Verification
            </button>
            <button
              onClick={() => navigate('/checkout')}
              className="flex-1 border-2 border-brand-darkSage text-brand-darkSage py-3.5 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-darkSage hover:text-white transition-all flex items-center justify-center gap-2"
            >
              Return to Checkout
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;